/**
 * HeatmapTile — 7×24 day-of-week × hour-of-day intensity grid.
 *
 * Pure HTML+CSS render (no chart library). Each cell is a coloured box whose
 * indigo saturation scales with its share of the column-max. Hovering shows
 * day + hour + value via Chakra Tooltip.
 *
 * Backend response shape (renderHeatmap):
 *   {
 *     chart: 'heatmap_dow_hour',
 *     metric: 'count' | 'unique_users',
 *     event,
 *     cells: [{ dow: 1..7, hour: 0..23, value }],   // dense 168-cell grid
 *     max_value, total_value,
 *   }
 */
import React, { useMemo } from 'react';
import { Box, Text, Tooltip } from '@chakra-ui/react';
import { PRIMARY_BG } from '../../../theme/brand';

const DOW_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
// 24-hour labels — show every hour. Owner asked for full granularity so the
// peak / quiet bands can be read off the axis without counting cells.
const HOUR_TICKS = Array.from({ length: 24 }, (_, h) => h);

const C_BRAND   = PRIMARY_BG;   // xNotify brand blue — matches active link in top nav (var(--brand-primary) = Tailwind blue-600)
const C_INDIGO  = C_BRAND;     // alias kept for readability
const C_TEXT    = '#2A2A2F';
const C_GREY3   = '#8F8F91';
const C_BG      = '#F6F6F6';
const C_BORDER  = '#E5E7EB';

function fmtCompact(n) {
  if (n == null) return '—';
  const x = Number(n);
  if (Math.abs(x) >= 1e9) return (x / 1e9).toFixed(2) + 'B';
  if (Math.abs(x) >= 1e6) return (x / 1e6).toFixed(2) + 'M';
  if (Math.abs(x) >= 1e3) return (x / 1e3).toFixed(1) + 'K';
  return Math.round(x).toLocaleString();
}

/**
 * Multi-hue heatmap palette — five stops covering the full intensity range
 * with distinct colours so adjacent cells of similar magnitude are visually
 * distinguishable. Classic "cool-to-hot" radar ramp:
 *
 *   light blue  → brand blue  → teal  → amber  → red
 *
 * Reads correctly in print, doesn't rely on saturation alone (which fails
 * on low values), and the cool/warm split makes "low" vs "high" instantly
 * obvious without needing the legend.
 */
const PALETTE = [
  { t: 0.00, rgb: [219, 234, 254] },   // var(--brand-tint-strong)  light blue   — sparse
  { t: 0.25, rgb: [ 59, 130, 246] },   // #3B82F6  brand blue
  { t: 0.50, rgb: [ 16, 185, 129] },   // #10B981  teal/green
  { t: 0.75, rgb: [245, 158,  11] },   // #F59E0B  amber/orange
  { t: 1.00, rgb: [220,  38,  38] },   // #DC2626  red          — peak
];

function lerp(a, b, t) { return Math.round(a + (b - a) * t); }

function paletteColour(t) {
  if (t <= 0) return PALETTE[0].rgb;
  if (t >= 1) return PALETTE[PALETTE.length - 1].rgb;
  for (let i = 0; i < PALETTE.length - 1; i++) {
    const lo = PALETTE[i], hi = PALETTE[i + 1];
    if (t >= lo.t && t <= hi.t) {
      const local = (t - lo.t) / (hi.t - lo.t);
      return [
        lerp(lo.rgb[0], hi.rgb[0], local),
        lerp(lo.rgb[1], hi.rgb[1], local),
        lerp(lo.rgb[2], hi.rgb[2], local),
      ];
    }
  }
  return PALETTE[PALETTE.length - 1].rgb;
}

/**
 * Map a value v in [0, max] to a CSS colour. Uses a square-root scale so
 * middle-range values stay visually weighty alongside peaks, then interpolates
 * through the multi-hue PALETTE with alpha layered on top so colours stay
 * soft / washed rather than crayon-solid. Low values are nearly transparent
 * (faint tint), high values approach full opacity (vivid but never saturated).
 */
function cellColour(v, max) {
  if (max <= 0 || v <= 0) return '#FAFAFB';
  const t = Math.sqrt(v / max);
  const [r, g, b] = paletteColour(t);
  // Peak cell stays SOLID red so the "where's the spike?" answer is instant.
  // Every other cell uses the alpha ramp (0.18 → 0.75) for the soft gradient.
  if (v >= max) return `rgb(${r}, ${g}, ${b})`;
  const alpha = 0.18 + 0.57 * t;
  return `rgba(${r}, ${g}, ${b}, ${alpha.toFixed(3)})`;
}

export default function HeatmapTile({ data }) {
  // Index cells by dow|hour for O(1) lookup during render. Also track which
  // cell IS the peak so the header can name it ("Peak: 64.0K · Sat 18:00").
  const { grid, max, total, peakDow, peakHour } = useMemo(() => {
    const g = new Map();
    let mx = 0, tot = 0, pd = null, ph = null;
    for (const c of (data?.cells || [])) {
      g.set(`${c.dow}|${c.hour}`, c.value);
      if (c.value > mx) { mx = c.value; pd = c.dow; ph = c.hour; }
      tot += c.value;
    }
    return { grid: g, max: mx, total: tot, peakDow: pd, peakHour: ph };
  }, [data]);

  if (!data || !data.cells || data.cells.length === 0) {
    return <Text fontSize="sm" color="gray.500">No events in this time window.</Text>;
  }

  const metricLabel = data.metric === 'unique_users' ? 'Unique users' : 'Events';

  return (
    <Box style={{ fontFamily: 'Inter, system-ui, sans-serif', minHeight: 320 }}>
      {/* Summary row */}
      <Box mb={3} style={{ display: 'flex', alignItems: 'baseline', gap: 12 }}>
        <Text fontSize="12px" color="#6B7280">
          {metricLabel} for <b style={{ color: C_TEXT }}>{data.event}</b>, by day × hour
        </Text>
        <Text fontSize="12px" color={C_GREY3}>
          Total: <b style={{ color: C_TEXT }}>{fmtCompact(total)}</b>
          {' · '}
          Peak hour: <b style={{ color: C_TEXT }}>{fmtCompact(max)}</b>
          {peakDow != null && peakHour != null && (
            <span style={{ color: C_GREY3 }}>
              {' ('}
              <b style={{ color: C_TEXT }}>{DOW_LABELS[peakDow - 1]} {String(peakHour).padStart(2, '0')}:00</b>
              {')'}
            </span>
          )}
        </Text>
      </Box>

      {/* Hour-of-day labels (top row) */}
      <Box style={{ display: 'grid', gridTemplateColumns: '40px repeat(24, 1fr)', gap: 2, marginBottom: 4 }}>
        <span />
        {Array.from({ length: 24 }, (_, h) => (
          <Text key={h} fontSize="9px" color={C_GREY3} style={{ textAlign: 'center' }}>
            {HOUR_TICKS.includes(h) ? String(h).padStart(2, '0') : ''}
          </Text>
        ))}
      </Box>

      {/* 7 day rows */}
      {DOW_LABELS.map((label, dowIdx) => {
        const dow = dowIdx + 1; // 1=Mon..7=Sun
        return (
          <Box
            key={dow}
            style={{
              display: 'grid',
              gridTemplateColumns: '40px repeat(24, 1fr)',
              gap: 2,
              marginBottom: 2,
              alignItems: 'center',
            }}
          >
            <Text fontSize="10px" color={C_TEXT} fontWeight={500}>{label}</Text>
            {Array.from({ length: 24 }, (_, hour) => {
              const v = grid.get(`${dow}|${hour}`) || 0;
              const bg = cellColour(v, max);
              return (
                <Tooltip
                  key={hour}
                  label={
                    <Box style={{ fontSize: 11 }}>
                      <div><b>{label}, {String(hour).padStart(2, '0')}:00 – {String(hour).padStart(2, '0')}:59</b></div>
                      <div>{fmtCompact(v)} {metricLabel.toLowerCase()}</div>
                      {max > 0 && (
                        <div style={{ color: '#A0AEC0' }}>
                          {((v / max) * 100).toFixed(1)}% of peak
                        </div>
                      )}
                    </Box>
                  }
                  placement="top"
                  hasArrow
                  openDelay={200}
                  bg="#1F2937"
                  color="#FFFFFF"
                  borderRadius="md"
                  px={2} py={1}
                >
                  <Box
                    style={{
                      height: 22,
                      background: bg,
                      borderRadius: 3,
                      border: `1px solid ${v > 0 ? 'transparent' : C_BORDER}`,
                      cursor: v > 0 ? 'pointer' : 'default',
                      transition: 'transform 80ms ease, box-shadow 80ms ease',
                    }}
                    _hover={v > 0 ? {
                      transform: 'scale(1.08)',
                      boxShadow: '0 1px 4px rgba(0,0,0,0.18)',
                    } : undefined}
                  />
                </Tooltip>
              );
            })}
          </Box>
        );
      })}

      {/* Color scale legend — samples the multi-hue palette at evenly-spaced
          t values so the legend matches what's actually drawn in the cells. */}
      <Box mt={3} style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'flex-end' }}>
        <Text fontSize="10px" color={C_GREY3}>Less</Text>
        {[0.05, 0.25, 0.5, 0.75, 1.0].map((t) => {
          const [r, g, b] = paletteColour(t);
          const alpha = 0.18 + 0.57 * t;
          return (
            <Box key={t} style={{
              width: 18, height: 12, borderRadius: 2,
              background: `rgba(${r}, ${g}, ${b}, ${alpha.toFixed(3)})`,
              border: t <= 0.05 ? `1px solid ${C_BORDER}` : 'none',
            }} />
          );
        })}
        <Text fontSize="10px" color={C_GREY3}>More</Text>
      </Box>
    </Box>
  );
}
